import React from 'react';
import { authFetch } from '../lib/authFetch';
import { t } from '../lib/i18n';
import Icon from './Icon';

/**
 * AppSidebar — Left navigation sidebar.
 *
 * Props:
 *   view              — current active view id
 *   setView           — setter for view
 *   lang              — 'no' | 'pl'
 *   setLang           — setter for lang
 *   sidebarCollapsed  — boolean
 *   setSidebarCollapsed — setter
 *   batchDoneCount    — number (badge on dashboard)
 *   batchRunning      — boolean (pulse animation)
 *   userRole          — 'admin' | 'auditor'
 *
 * Extracted from index.html ~line 1727.
 */
const AppSidebar = ({ view, setView, lang, setLang, sidebarCollapsed, setSidebarCollapsed, batchDoneCount, batchRunning, userRole, feedbackNewCount = 0 }) => {
    // P0 fix (2026-04-25): wszystkie label przez t() — nie hardcoded NO ("Varslingsstatus", "Historikk").
    // Decyzja: NO domyślnie (firma+klienci NO), PL toggle dostępny w footerze.
    const allNavItems = [
        { id: 'my-firms', icon: 'layout-dashboard', label: t('nav_my_firms'), auditorOnly: true },
        { id: 'batch-dashboard', icon: 'bar-chart-3', label: t('nav_dashboard'), adminOnly: true },
        { id: 'admin-work-activity', icon: 'clipboard-check', label: t('nav_work_activity'), adminOnly: true },
        { id: 'admin-notifications', icon: 'inbox', label: t('nav_notifications'), adminOnly: true },
        { id: 'reports', icon: 'pie-chart', label: t('nav_reports'), adminOnly: true },
        { id: 'home', icon: 'users', label: t('nav_home'), adminOnly: true },
        { id: 'settings', icon: 'settings', label: t('nav_settings') },
    ];
    const navItems = allNavItems.filter(i => {
        if (i.adminOnly && userRole !== 'admin') return false;
        if (i.auditorOnly && userRole === 'admin') return false;
        return true;
    });
    const [narrow, setNarrow] = React.useState(() => window.innerWidth < 640);
    React.useEffect(() => {
        const resize = () => setNarrow(window.innerWidth < 640);
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);
    const collapsed = narrow || sidebarCollapsed;
    const toggleCollapse = () => {
        if (narrow) return;
        const next = !collapsed;
        setSidebarCollapsed(next);
        try { localStorage.setItem('auditor-sidebar-collapsed', String(next)); } catch(e) {}
    };
    return (
        <aside style={{ width: collapsed ? '60px' : '240px', flexShrink: 0, transition: 'width 0.2s ease' }}
            className="bg-white border-r border-[#E5E7EB] flex flex-col h-screen overflow-hidden">
            {/* Header */}
            <div className="h-14 flex items-center px-3 border-b border-[#E5E7EB] flex-shrink-0">
                {!collapsed ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-[#AA5D26] flex items-center justify-center flex-shrink-0">
                            <Icon name="shield-check" className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="font-semibold text-sm font-heading text-[#1A1A2E] truncate">MTA Group</span>
                            <span className="text-xs text-[#9CA3AF] truncate">Audytor PRO</span>
                        </div>
                    </div>
                ) : (
                    <div className="w-7 h-7 rounded-lg bg-[#AA5D26] flex items-center justify-center mx-auto">
                        <Icon name="shield-check" className="w-4 h-4 text-white" />
                    </div>
                )}
            </div>
            {/* Nav items */}
            <nav className="flex-1 py-2 px-3 space-y-1 overflow-y-auto">
                {navItems.map(item => (
                    <button key={item.id}
                        onClick={() => React.startTransition(() => setView(item.id))}
                        title={collapsed ? item.label : undefined}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            view === item.id
                                ? 'bg-[#F5E6D3] text-[#AA5D26]'
                                : 'text-[#4B5563] hover:bg-[#F5F5F5] hover:text-[#1A1A2E]'
                        } ${collapsed ? 'justify-center' : ''}`}>
                        <span className="flex-shrink-0 relative">
                            <Icon name={item.icon} className="w-5 h-5" />
                            {item.id === 'batch-dashboard' && (batchDoneCount > 0 || batchRunning) && (
                                <span className={`absolute -top-1.5 -right-1.5 w-4 h-4 text-[9px] font-bold rounded-full flex items-center justify-center leading-none ${batchRunning ? 'animate-pulse' : ''} ${view === 'batch-dashboard' ? 'bg-[#AA5D26] text-white' : batchRunning ? 'bg-[#AA5D26] text-white' : 'bg-[#AA5D26] text-white'}`}>
                                    {batchRunning ? '\u23F3' : batchDoneCount > 9 ? '9+' : batchDoneCount}
                                </span>
                            )}
                            {item.id === 'admin-notifications' && feedbackNewCount > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-0.5 text-[9px] font-bold rounded-full flex items-center justify-center leading-none bg-red-500 text-white">
                                    {feedbackNewCount > 9 ? '9+' : feedbackNewCount}
                                </span>
                            )}
                        </span>
                        {!collapsed && <span className="truncate">{item.label}</span>}
                    </button>
                ))}
            </nav>
            {/* Footer */}
            <div className="border-t border-[#E5E7EB] p-3 space-y-1 flex-shrink-0">
                <button onClick={() => window.open('/instrukcja', '_blank', 'noopener')}
                    title={collapsed ? (lang === 'no' ? 'Hjelp' : 'Pomoc') : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#6B7280] hover:bg-[#F5F5F5] hover:text-[#1A1A2E] transition-colors ${collapsed ? 'justify-center' : ''}`}>
                    <Icon name="book-open" className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="truncate">{lang === 'no' ? 'Hjelp' : 'Pomoc'}</span>}
                </button>
                <button onClick={() => setLang(lang === 'no' ? 'pl' : 'no')}
                    title={collapsed ? (lang === 'no' ? 'Polski' : 'Norsk') : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#6B7280] hover:bg-[#F5F5F5] hover:text-[#1A1A2E] transition-colors ${collapsed ? 'justify-center' : ''}`}>
                    <Icon name="globe" className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="truncate">{lang === 'no' ? 'Polski' : 'Norsk'}</span>}
                </button>
                <button onClick={() => {
                    // FE2 fix (2026-04-25): full client-side cleanup przy logout — shared device leak protection.
                    // Dane operacyjne sa online; czyscimy tylko lokalne preferencje/sesje przegladarki.
                    authFetch('/api/auth/logout', { method: 'POST', body: '{}' }).finally(async () => {
                        try { sessionStorage.clear(); } catch(e) {}
                        try {
                            ['audit-disabled-rules','audit-team','audit-last','audit-rules-collapsed','audit-lang','audit-darkmode']
                                .forEach(k => { try { localStorage.removeItem(k); } catch {} });
                        } catch(e) {}
                        window.location.reload();
                    });
                }}
                    title={collapsed ? t('auth_logout') : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#6B7280] hover:bg-red-50 hover:text-red-600 transition-colors ${collapsed ? 'justify-center' : ''}`}>
                    <Icon name="log-out" className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="truncate">{t('auth_logout')}</span>}
                </button>
                {!narrow && <button onClick={toggleCollapse}
                    title={collapsed ? t('sidebar_show') : t('sidebar_hide')}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#6B7280] hover:bg-[#F5F5F5] hover:text-[#1A1A2E] transition-colors ${collapsed ? 'justify-center' : ''}`}>
                    <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="text-xs truncate">{t('collapse_sidebar')}</span>}
                </button>}
            </div>
        </aside>
    );
};

export default AppSidebar;
